import { FC, useContext, useEffect, useState } from "react";
import { CompetitionContext } from "../../../CompetitionContext";
import { useGameTime } from "../../../hooks/useGameTime";
import { useUpcomingGames } from "../../../hooks/useUpcomingGames";
import { useDebouncedCurrentGame } from "../../../hooks/useDebouncedCurrentGame";
import { useGameBefore } from "../../../hooks/useGameBefore";
import { getPresentationParamsForOrigin } from "../../../getPresentationParamsForOrigin";
import { useCompetition } from "../../../hooks/useCompetition";
import { DateTime } from "luxon";
import { components } from "../../../openapi/generated-types";
import {
  GamePreviewCard,
  GamePreviewLabel,
  GamePreviewPlaceholder,
} from "../Components/Overview/GamePreviewCard";
import { LiveGameCard } from "../Components/Overview/LiveGameCard";
import { GroupStandingsCard } from "../Components/Overview/GroupStandingsCard";

export const CompetitionOverviewDisplay: FC = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const groupRegexParam = queryParams.get("groupRegex");
  const groupRegex = groupRegexParam
    ? new RegExp(groupRegexParam)
    : new RegExp(".*");

  const competitionId = useContext(CompetitionContext);
  const competition = useCompetition(competitionId);

  const groups =
    competition.data?.groups.filter((g) => groupRegex.test(g.name)) ?? [];

  const currentGame = useDebouncedCurrentGame(competitionId, 10 * 1000);
  const currentGameTime = useGameTime(currentGame.data?.id);

  const upcomingGames = useUpcomingGames(competitionId, false);
  const nextGame = upcomingGames
    ? upcomingGames.find((g) => g.id !== currentGame.data?.id)
    : null;

  const gameBefore = useGameBefore(competitionId);
  const [debouncedGameBefore, setDebouncedGameBefore] = useState<
    components["schemas"]["Game"] | null
  >();

  useEffect(() => {
    if (!debouncedGameBefore) {
      setDebouncedGameBefore(gameBefore);
      return;
    }

    const timerId = setTimeout(() => {
      setDebouncedGameBefore(gameBefore);
    }, 60_000);

    return () => {
      clearTimeout(timerId);
    };
  }, [gameBefore]);

  const brandColors = getPresentationParamsForOrigin().colors.brand;

  return (
    <div
      style={{
        width: 1920,
        height: 1080,
        backgroundColor: "rgb(50,50,50)",
        padding: "50px",
        display: "grid",
        gap: "25px",
        gridTemplateRows: "auto auto auto 1fr",
        fontFamily: "Chivo",
        lineHeight: 1,
      }}
    >
      {/* Branding */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src={getPresentationParamsForOrigin().logo.light}
          alt="Logo"
          style={{ height: 75 }}
        />
      </div>

      {/* Last/Next Game */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "25px",
        }}
      >
        <div style={{ borderBottom: `5px solid ${brandColors[7]}` }}>
          <GamePreviewLabel label="Vorheriges Spiel" />
          {debouncedGameBefore ? (
            <GamePreviewCard game={debouncedGameBefore} />
          ) : (
            <GamePreviewPlaceholder message="Es wurde noch kein Spiel gespielt" />
          )}
        </div>

        <div style={{ borderBottom: `5px solid ${brandColors[7]}` }}>
          <GamePreviewLabel
            label={`Nächstes Spiel${nextGame ? ` (${DateTime.fromISO(nextGame.dateTime).toFormat("ccc., HH:mm")} Uhr)` : ""}`}
          />
          {nextGame ? (
            <GamePreviewCard game={nextGame} />
          ) : (
            <GamePreviewPlaceholder message="Kein weiteres Spiel" />
          )}
        </div>
      </div>

      {/* Current Game */}
      {currentGame.data && (
        <LiveGameCard
          game={currentGame.data}
          time={currentGameTime}
          competitionId={competitionId}
        />
      )}

      {/* Leaderboards */}
      {competition.isSuccess && (
        <div
          style={{
            display: "grid",
            gridAutoFlow: "column",
            gridAutoColumns: "1fr",
            gap: "25px",
          }}
        >
          {groups.map((g) => (
            <div key={g.id}>
              <GroupStandingsCard
                competitionId={competitionId}
                groupId={g.id}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
